import { Button as MuiButton } from '@mui/material';
import React from 'react';
import { Link } from 'react-router-dom';
import { useTracking } from '../../../../react-components/tracking';

interface ToPath {
  /**
   * A string representing the path to link to
   */
  pathname: string;
  /**
   * A string representation of query parameters
   */
  search?: string;
}

/**
 * A button that debounces clicks for a given delay to
 * prevent click spamming.
 */
export interface LinkButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * The path to link to
   */
  to: ToPath;
  /**
   * Whether the button is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * The color of the button
   * @default 'primary'
   */
  color?:
    | 'inherit'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'error'
    | 'info'
    | 'warning';
  /**
   * Variant of the button
   * @default 'contained'
   */
  variant?: 'text' | 'outlined' | 'contained';
  /**
   * The size of the button
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * An icon to display at the start of the button
   * @default null
   */
  startIcon?: React.ReactNode;
  /**
   * A className to apply to the underlying button for styling
   */
  className?: string;
  /**
   * Object containing sx styles
   * @default null
   */
  sx?: object;
  /**
   * The content of the button; nullable if only using
   * a 'startIcon' as the content
   */
  children?: React.ReactNode;
  /**
   * Optional tracking name for DataDog tracking. If not provided, defaults to 'link-button'
   */
  trackingName?: string;
}

export const LinkButton = ({
  to,
  color = 'primary',
  variant = 'contained',
  size = 'medium',
  className,
  disabled = false,
  startIcon = null,
  sx,
  children = null,
  trackingName,
  ...props
}: LinkButtonProps) => {
  const { generateTrackingProps } = useTracking();

  // Generate tracking props with fallback to 'link-button' if no trackingName provided
  const trackingProps = generateTrackingProps(trackingName || 'link-button');

  return (
    <MuiButton
      component={Link as any}
      to={to}
      color={color}
      variant={variant}
      disabled={disabled}
      className={className}
      type="button"
      size={size}
      startIcon={startIcon}
      sx={sx}
      role="button"
      {...trackingProps}
      {...props}
    >
      {children}
    </MuiButton>
  );
};
